"use client";

import {
  STATSIG_EVENTS,
  useRoutePathname,
  useStatsigClient,
  type RegisteredStatsigEvent,
} from "@repo/statsig";
import Image from "next/image";
import BrandedActionButton from "src/components/branded-action-button";
import { BodyText, HeadlineText } from "src/components/KinsaTypography";
import { Drawer } from "vaul";

interface ConversionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onContinueWithRx: () => void;
  billingPeriod?: "monthly" | "yearly";
  rxProductTitle: string;
  pedsProductTitle: string;
}

/**
 * Conversion drawer component shown when Pediatric Support is not available
 * Displays Fast RX as the available alternative with product details
 */
export function ConversionDrawer({
  isOpen,
  onClose,
  onContinueWithRx,
  billingPeriod = "monthly",
  rxProductTitle: RxProductTitle,
  pedsProductTitle: PedsProductTitle,
}: ConversionDrawerProps) {
  // Hardcoded prices per Figma design: $29/month, $199/year
  const rxPrice = billingPeriod === "yearly" ? 199 : 29;
  const { logEvent } = useStatsigClient();
  const locationData = useRoutePathname();

  const handleContinueWithRx = () => {
    // Log Statsig event
    const statsigEvent: RegisteredStatsigEvent = {
      eventName: "rescued_to_rx",
      metadata: {
        ...locationData,
        product: RxProductTitle,
        billingPeriod,
      },
    };
    logEvent(statsigEvent);

    // Call the original handler
    onContinueWithRx();
  };

  const handleDismiss = () => {
    // Log Statsig event
    const statsigEvent: RegisteredStatsigEvent = {
      eventName: "subscription_flow_abandoned",
      value: "Continue With RX Drawer",
      metadata: {
        ...locationData,
        product: RxProductTitle,
        billingPeriod,
      },
    };
    logEvent(statsigEvent);
    logEvent({
      eventName: STATSIG_EVENTS.UI_DISMISSED,
      value: "checkout",
      metadata: { ...locationData },
    });

    // Dismiss the drawer
    onClose();
  };

  return (
    <Drawer.Root open={isOpen} onOpenChange={handleDismiss} dismissible={true}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/40 z-[99999]" />
        <Drawer.Content className="fixed bottom-0 left-0 right-0 z-[100000] outline-none flex flex-col bg-teal-1000 rounded-t-[16px] border-t border-[rgba(255,255,255,0.09)] backdrop-blur-[12.5px] pb-[var(--felt-safe-area-bottom,0px)]">
          <div className="flex flex-col gap-[24px] items-start p-[24px] pb-0 w-full">
            {/* Header Section */}
            <div className="flex flex-col gap-[8px] items-start w-full">
              <Drawer.Title className="sr-only">
                Coming soon to your state! - {PedsProductTitle} not available,{" "}
                {RxProductTitle} available
              </Drawer.Title>
              <Drawer.Description className="sr-only">
                {PedsProductTitle} is not yet available in your area, but
                we&apos;ll contact you once the service is ready. You will not
                be charged. {RxProductTitle}
                is available right now.
              </Drawer.Description>

              <HeadlineText className="text-white text-center text-[20px] leading-[28px] tracking-[0.2px] w-full">
                Coming soon to your state!
              </HeadlineText>
              <BodyText className="text-gray-100 text-left text-[15px] leading-[22px] tracking-[0.25px] w-full">
                {PedsProductTitle} is not yet available in your state, but
                you&apos;ll be the first to know when it is — and get exclusive
                early access.
                <br />
                <br />
                In the meantime, {RxProductTitle} is ready to help with
                prescriptions for the whole family.
              </BodyText>
            </div>

            {/* Plan Info Section */}
            <div className="flex flex-col gap-[8px] items-start w-full">
              <BodyText className="text-white text-[16px] leading-[24px] tracking-[0.08px]">
                Available right now:
              </BodyText>

              {/* Product Card */}
              <div className="border-[#00a1c5] border-[1.5px] border-solid rounded-[16px] p-[16px] w-full relative overflow-hidden">
                {/* Product Image - positioned behind text */}
                <div className="absolute right-[16px] top-[16px] size-[52px] z-0">
                  <Image
                    alt={`${RxProductTitle} illustration`}
                    src="/rx-asset-small.png"
                    className="size-full object-contain"
                    width={52}
                    height={52}
                    unoptimized
                  />
                </div>

                {/* Plan Text - positioned above image */}
                <div className="relative z-10">
                  <div className="flex flex-col gap-[2px] items-start flex-1 pr-[48px]">
                    <HeadlineText className="text-white text-[20px] leading-[28px] tracking-[0.2px]">
                      {RxProductTitle}
                    </HeadlineText>
                    <BodyText className="text-slate-50 text-[15px] leading-[22px] tracking-[0.25px]">
                      The easiest way to get prescriptions
                    </BodyText>
                    <div className="flex flex-col justify-end leading-[0] mt-[2px]">
                      <p className="text-[15px] leading-[22px]">
                        <span className="text-white font-normal">
                          ${rxPrice}
                        </span>
                        <span className="text-[#6b8085] font-normal">
                          /{billingPeriod === "yearly" ? "year" : "month"}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons Container */}
            <div className="flex flex-col gap-[16px] items-start w-full pb-[24px]">
              <BrandedActionButton
                text="GET FAST RX INSTEAD"
                onPress={handleContinueWithRx}
                eventData={{
                  action: "conversion_drawer_continue_rx",
                  product: RxProductTitle,
                  billingPeriod,
                }}
                className="!bg-[#00a1c5] hover:!bg-[#00a1c5] h-[44px] w-full rounded-[99px] text-[15px] font-medium tracking-[1.5px] uppercase"
                variant="primary"
              />
              <button
                type="button"
                onClick={handleDismiss}
                className="h-[44px] w-full rounded-[99px] flex items-center justify-center px-[24px] py-[14px]"
              >
                <BodyText className="text-[#00a1c5] text-[15px] leading-[22px] tracking-[1.5px] uppercase">
                  DISMISS
                </BodyText>
              </button>
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
